import { EntityKind } from "../protocol/kinds.ts";
import { QUARTER_RATE_TICKS, SNAPSHOT_RING } from "../protocol/limits.ts";
import { trueCoordinate } from "../movement/true-point.ts";
import { BLOCKING, SIXTEENTHS, stepMovement, Direction, type CollisionGrid, type Facing } from "../movement/step.ts";
import type { EntityState } from "../protocol/snapshot.ts";

/**
 * Remote entity interpolation (CLIENT.md section 4, ADR-032 item 5 and its amendment of 2026-10-03): views keyed by
 * tick, rendered `RENDER_DELAY_TICKS` behind the render clock (`RenderClock`), positions interpolated between the
 * bracketing views in fractional sixteenths, held when nothing newer exists, then, while no send of the entity is
 * overdue (`sample`), extrapolated by fractions of a tick at most `EXTRAPOLATE_TICKS` along the last velocity, between
 * whole-tick waypoints each clipped by the shared movement step so nothing crosses a blocking cell. The renderer rounds.
 * A view is held at its true point (`trueCoordinate`, ADR-116's amendment of 2026-10-07): the lattice point and the
 * offsets the view carries, so a walk whose lattice steps are 2 and 3 sixteenths is drawn at one pace, turning, sliding
 * and stopping included. An entity whose view carries no offsets, a creature and a thing among them, is held on its
 * lattice point. An entity is never drawn behind the point last drawn while its motion and its facing hold (P2.20-T14,
 * `holdAxis`): an extrapolation past a quarter-rate view that the next view falls short of, as at a run's end or a
 * stop, is held where it was drawn until the drawn line passes it, so a still frame reads as a stop and no step back
 * reads as a hitch. A stop held so stands up to `EXTRAPOLATE_TICKS` of its pace past its true point until it moves.
 */

/**
 * Ticks the render clock trails the newest applied tick.
 */
export const RENDER_DELAY_TICKS = 2;

/**
 * Most ticks to extrapolate past the newest view.
 */
export const EXTRAPOLATE_TICKS = 2;

/**
 * The longest move between two views that is drawn as a move, in sixteenths (2 tiles, P2.1-T4): a character that
 * moved farther along either axis, as a respawn at a shrine does, snaps to where it now stands.
 */
export const SNAP_SIXTEENTHS = 2 * SIXTEENTHS;

/**
 * What the renderer draws for an entity: where, and the facing, the animation bits and the running bit (ADR-116
 * decision 14) of the view the position is drawn toward, so a character's strip changes where its pace does.
 */
export interface RenderState {
  readonly x: number;
  readonly y: number;
  readonly facing: Facing;
  readonly anim: number;
  readonly running: boolean;
}

/**
 * A view held for an entity: the tick, the state, and where the state is drawn, its true point, in sixteenths.
 */
interface HeldState {
  readonly tick: number;
  readonly state: EntityState;
  readonly x: number;
  readonly y: number;
}

/**
 * Where an entity was last drawn, and how: the render clock, the point, the facing, and the sign of its views' motion
 * along each axis then. One a handle, made when its first view is recorded and written in place by every sample.
 */
interface DrawnPoint {
  drawn: boolean;
  clock: number;
  x: number;
  y: number;
  facing: Facing;
  signX: number;
  signY: number;
}

/**
 * The interpolator over one map server's entities.
 */
export class Interpolator {
  private readonly history = new Map<number, HeldState[]>();
  private readonly drawnPoints = new Map<number, DrawnPoint>();
  private newestTick = 0;
  private readonly probe = { x: 0, y: 0, facing: 0 as Facing };
  private readonly point = { x: 0, y: 0 };

  /**
   * @param grid The client's collision grid, for clipping extrapolation.
   */
  public constructor(private readonly grid: CollisionGrid) {}

  /**
   * Records the applied view of a tick. Entities absent from the view keep their earlier states, the newest one
   * however old until `forget`, since an entity that stays unchanged is not recorded again; views older than
   * `SNAPSHOT_RING` behind the newest tick are dropped when a newer one is held. An entity that
   * moved more than `SNAP_SIXTEENTHS` along either axis since its last view forgets the views before this one, so it
   * is drawn where it now stands, never between, and never extrapolated across the jump. An entity whose last view is
   * more than `QUARTER_RATE_TICKS` older, or whose last view stood where the one before it did (a turn or an animation
   * in place), stood there until at least the tick before this one (ADR-032's note of 2026-10-10): that view is held
   * again at the tick before, so its move is drawn over one tick at its own pace, not across the whole stand, and the
   * ring's trimming cannot lose where it stood.
   *
   * @param tick The tick.
   * @param states Every entity state in the view.
   */
  public record(tick: number, states: readonly EntityState[]): void {
    this.newestTick = Math.max(this.newestTick, tick);
    for (const state of states) {
      let held = this.history.get(state.handle);
      if (held === undefined) {
        held = [];
        this.history.set(state.handle, held);
      }
      let mark = this.drawnPoints.get(state.handle);
      if (mark === undefined) {
        mark = { drawn: false, clock: 0, x: 0, y: 0, facing: state.facing, signX: 0, signY: 0 };
        this.drawnPoints.set(state.handle, mark);
      }
      const last = held[held.length - 1];
      if (last !== undefined && (Math.abs(state.x - last.state.x) > SNAP_SIXTEENTHS || Math.abs(state.y - last.state.y) > SNAP_SIXTEENTHS)) {
        held.length = 0;
        mark.drawn = false;
      } else if (last !== undefined && tick - 1 > last.tick && (tick - last.tick > QUARTER_RATE_TICKS || stoodAt(held[held.length - 2], last))) {
        held.push({ tick: tick - 1, state: last.state, x: last.x, y: last.y });
      }
      held.push({ tick, state, x: trueCoordinate(state.x, state.offsetX ?? 0), y: trueCoordinate(state.y, state.offsetY ?? 0) });
    }
    const oldest = this.newestTick - SNAPSHOT_RING;
    for (const held of this.history.values()) {
      while (held.length > 1 && (held[0] as HeldState).tick < oldest) {
        held.shift();
      }
    }
  }

  /**
   * How many records are held for a handle: a figure a crossing holds is handed to the collection drawing this
   * interpolator only once it holds two, so the sample lies between them on the path the held figure was carried along
   * (ADR-147 decision 15).
   *
   * @param handle The handle.
   * @returns The count, 0 for a handle never recorded or forgotten.
   */
  public depth(handle: number): number {
    return this.history.get(handle)?.length ?? 0;
  }

  /**
   * Forgets an entity that left the view, so it samples null until a view records it again.
   *
   * @param handle Entity handle.
   */
  public forget(handle: number): void {
    this.history.delete(handle);
    this.drawnPoints.delete(handle);
  }

  /**
   * The state to draw for an entity at a render clock in ticks (the newest applied tick plus the frame
   * fraction); the delay is applied here. Past its newest view an entity is extrapolated while fewer ticks have
   * been recorded since that view than the gap between its last two views, and fewer than `QUARTER_RATE_TICKS`: an
   * entity sent every tick and left out of the next view did not change, so a character that stopped is held where
   * it stands, while one sent at a quarter rate (ADR-034 decision 3) keeps moving between its sends. A quarter-rate
   * entity that stops may be drawn up to `EXTRAPOLATE_TICKS` past its stop, and is then held there rather than
   * stepped back (`holdAxis`) until it moves again.
   *
   * @param clock Render clock in ticks, fractional.
   * @param handle Entity handle.
   * @returns The render state, or null for an entity with no held view.
   */
  public sample(clock: number, handle: number): RenderState | null {
    const held = this.history.get(handle);
    if (held === undefined || held.length === 0) {
      return null;
    }
    const time = clock - RENDER_DELAY_TICKS;
    const last = held[held.length - 1] as HeldState;
    if (time >= last.tick) {
      const previous = held[held.length - 2];
      const span = previous === undefined ? 1 : last.tick - previous.tick;
      const point = this.point;
      if (this.newestTick - last.tick < Math.min(span, QUARTER_RATE_TICKS)) {
        this.extrapolate(held, time - last.tick);
      } else {
        point.x = last.x;
        point.y = last.y;
      }
      if (previous === undefined) {
        const mark = this.drawnPoints.get(handle);
        return this.drawn(handle, clock, last.state, point.x, point.y, mark?.signX ?? 0, mark?.signY ?? 0);
      }
      return this.drawn(handle, clock, last.state, point.x, point.y, Math.sign(last.x - previous.x), Math.sign(last.y - previous.y));
    }
    let after = 0;
    while (after < held.length && (held[after] as HeldState).tick <= time) {
      after += 1;
    }
    if (after === 0) {
      const first = held[0] as HeldState;
      return this.drawn(handle, clock, first.state, first.x, first.y, 0, 0);
    }
    const a = held[after - 1] as HeldState;
    const b = held[after] as HeldState;
    const t = (time - a.tick) / (b.tick - a.tick);
    return this.drawn(handle, clock, b.state, a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, Math.sign(b.x - a.x), Math.sign(b.y - a.y));
  }

  /**
   * Holds a sample at the point last drawn along each axis it would step back on (P2.20-T14), and records what is
   * drawn. An axis is held while the render clock has not gone back, the facing is the one last drawn, the views move
   * along it the way they moved when it was last drawn, and the sample lies behind the drawn point by less than
   * `SNAP_SIXTEENTHS`. A turn of the views' motion or of the facing, a clock set back and a snap release it at once.
   *
   * @param handle Entity handle.
   * @param clock Render clock in ticks, fractional.
   * @param state The view the sample is drawn toward: its facing, animation and running bit.
   * @param sampledX The sample's x, in sixteenths.
   * @param sampledY The sample's y.
   * @param signX The sign of the views' motion along x: the two views the sample lies between, or the last two, or the
   *   sign last drawn when the newest view is the only one held, since nothing has moved since.
   * @param signY The same along y.
   * @returns The render state, its point held where it would step back; the one object a sample makes.
   */
  private drawn(handle: number, clock: number, state: EntityState, sampledX: number, sampledY: number, signX: number, signY: number): RenderState {
    const mark = this.drawnPoints.get(handle);
    if (mark === undefined) {
      return render(state, sampledX, sampledY);
    }
    const holds = mark.drawn && clock >= mark.clock && state.facing === mark.facing;
    const x = holds ? holdAxis(sampledX, mark.x, signX, mark.signX) : sampledX;
    const y = holds ? holdAxis(sampledY, mark.y, signY, mark.signY) : sampledY;
    mark.drawn = true;
    mark.clock = clock;
    mark.x = x;
    mark.y = y;
    mark.facing = state.facing;
    mark.signX = signX;
    mark.signY = signY;
    return render(state, x, y);
  }

  /**
   * The newest tick recorded.
   *
   * @returns The tick, 0 before any.
   */
  public get newest(): number {
    return this.newestTick;
  }

  /**
   * Continues past the newest view along the last velocity for at most `EXTRAPOLATE_TICKS`: whole-tick waypoints, each
   * a shared movement step clipped by the grid, and the fractional tick drawn on the line between the two around it,
   * whose ends are clear of every blocking cell; the straight line may cut a corner by under a step, as interpolation does.
   * The velocity is the last two views' drawn points apart over the ticks between them: exact for a player, whose true
   * point moves at its own pace wherever its views happen to lie, and cut to whole sixteenths for every other kind,
   * whose steps are whole. The point is written to the interpolator's scratch point (`point`), which the caller reads
   * before the next sample.
   *
   * @param held The entity's views.
   * @param beyond Ticks past the newest view, fractional.
   */
  private extrapolate(held: readonly HeldState[], beyond: number): void {
    const last = held[held.length - 1] as HeldState;
    const point = this.point;
    if (held.length < 2 || beyond <= 0) {
      point.x = last.x;
      point.y = last.y;
      return;
    }
    const previous = held[held.length - 2] as HeldState;
    const span = last.tick - previous.tick;
    const paced = last.state.kind === EntityKind.Player;
    const vx = paced ? (last.x - previous.x) / span : Math.trunc((last.x - previous.x) / span);
    const vy = paced ? (last.y - previous.y) / span : Math.trunc((last.y - previous.y) / span);
    const reach = Math.min(EXTRAPOLATE_TICKS, beyond);
    const whole = Math.floor(reach);
    let position = { x: last.x, y: last.y, facing: last.state.facing };
    for (let i = 0; i < whole; i += 1) {
      position = this.waypoint(position, vx, vy);
    }
    const share = reach - whole;
    if (share === 0) {
      point.x = position.x;
      point.y = position.y;
      return;
    }
    const ahead = this.waypoint(position, vx, vy);
    point.x = position.x + (ahead.x - position.x) * share;
    point.y = position.y + (ahead.y - position.y) * share;
  }

  /**
   * One tick of extrapolation: the velocity's step along x, then along y, each through the shared step's blocking rules.
   *
   * @param from The waypoint before.
   * @param vx Sixteenths per tick across.
   * @param vy Sixteenths per tick down.
   * @returns The next waypoint.
   */
  private waypoint(from: { x: number; y: number; facing: Facing }, vx: number, vy: number): { x: number; y: number; facing: Facing } {
    const x = this.clipAxis(from, vx, true);
    return { x, y: this.clipAxis({ x, y: from.y, facing: from.facing }, vy, false), facing: from.facing };
  }

  /**
   * Moves along one axis by a velocity through the shared step's blocking rules. The step is taken between the lattice
   * points nearest the position and where the velocity would carry it: when nothing blocks it the position moves the
   * whole velocity, fraction and all, and when a cell does it stops flush on the lattice point the step reached.
   *
   * @param from Current position, in sixteenths, fractional.
   * @param velocity Sixteenths per tick along the axis, fractional.
   * @param horizontal Which axis.
   * @returns The new coordinate.
   */
  private clipAxis(from: { x: number; y: number; facing: Facing }, velocity: number, horizontal: boolean): number {
    const start = horizontal ? from.x : from.y;
    if (velocity === 0) {
      return start;
    }
    const target = start + velocity;
    const cell = Math.round(start);
    const goal = Math.round(target);
    if (goal === cell) {
      return target;
    }
    const dir = horizontal ? (velocity > 0 ? Direction.Right : Direction.Left) : velocity > 0 ? Direction.Down : Direction.Up;
    const probe = this.probe;
    probe.x = Math.round(from.x);
    probe.y = Math.round(from.y);
    probe.facing = from.facing;
    const moved = stepMovement(probe, dir, this.grid, BLOCKING, Math.abs(goal - cell));
    const reached = horizontal ? moved.x : moved.y;
    return reached === goal ? target : reached;
  }
}

/**
 * Whether an entity's last view stood where the view before it did.
 *
 * @param before The view before the last, if one is held.
 * @param last The last view.
 * @returns True when both are held at one point.
 */
function stoodAt(before: HeldState | undefined, last: HeldState): boolean {
  return before !== undefined && before.x === last.x && before.y === last.y;
}

/**
 * One axis of a sample, held at the point last drawn when it would step back against the views' motion (P2.20-T14).
 *
 * @param sampled The sample's coordinate.
 * @param drawn The coordinate last drawn.
 * @param sign The sign of the views' motion along the axis now.
 * @param drawnSign Its sign when the coordinate was last drawn.
 * @returns The coordinate to draw: the one last drawn while the motion holds and the sample lies behind it by less
 *   than `SNAP_SIXTEENTHS`, the sample otherwise.
 */
function holdAxis(sampled: number, drawn: number, sign: number, drawnSign: number): number {
  const behind = (drawn - sampled) * sign;
  return sign !== 0 && sign === drawnSign && behind > 0 && behind < SNAP_SIXTEENTHS ? drawn : sampled;
}

/**
 * Builds a render state from an entity state at a position.
 *
 * @param state Source of the facing, the animation and the running bit; a state that names no bit is not running.
 * @param x Position x.
 * @param y Position y.
 * @returns The render state.
 */
function render(state: EntityState, x: number, y: number): RenderState {
  return { x, y, facing: state.facing, anim: state.anim, running: state.running === true };
}
